import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Info, X, AlertTriangle } from "lucide-react";

const ToastContext = createContext(null);

const TOAST_DURATION_MS = 4000;

function createToastId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function ToastViewport({ toasts, onDismiss }) {
  return (
    <div className="toast-viewport" aria-live="polite" aria-atomic="true">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast-card toast-${toast.variant}`}
          role={toast.variant === "error" ? "alert" : "status"}
        >
          <div className="toast-icon" aria-hidden="true">
            {toast.variant === "success" ? (
              <CheckCircle2 size={18} />
            ) : toast.variant === "warning" ? (
              <AlertTriangle size={18} />
            ) : toast.variant === "error" ? (
              <AlertCircle size={18} />
            ) : (
              <Info size={18} />
            )}
          </div>
          <div className="toast-copy">
            {toast.title ? <p className="toast-title">{toast.title}</p> : null}
            <p className="toast-message">{toast.message}</p>
          </div>
          <button
            type="button"
            className="toast-dismiss"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(toast.id)}
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timeoutRefs = useRef(new Map());

  const dismissToast = useCallback((toastId) => {
    const timeoutId = timeoutRefs.current.get(toastId);

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutRefs.current.delete(toastId);
    }

    setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== toastId));
  }, []);

  const pushToast = useCallback(
    ({ title = "", message, variant = "info", duration = TOAST_DURATION_MS }) => {
      if (!message) {
        return "";
      }

      const id = createToastId();
      const nextToast = {
        id,
        title,
        message: String(message),
        variant,
      };

      setToasts((currentToasts) => [...currentToasts, nextToast]);

      const timeoutId = window.setTimeout(() => {
        dismissToast(id);
      }, duration);

      timeoutRefs.current.set(id, timeoutId);
      return id;
    },
    [dismissToast]
  );

  const value = useMemo(
    () => ({
      pushToast,
      dismissToast,
      showSuccess: (message, options = {}) =>
        pushToast({ ...options, message, variant: "success" }),
      showError: (message, options = {}) =>
        pushToast({ ...options, message, variant: "error" }),
      showInfo: (message, options = {}) =>
        pushToast({ ...options, message, variant: "info" }),
      showWarning: (message, options = {}) =>
        pushToast({ ...options, message, variant: "warning" }),
    }),
    [dismissToast, pushToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider.");
  }

  return context;
}
